import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Patient, HealthRecord, VitalHistory, ImportantNote, ResidentVisitation } from '../types';
import VitalAnalyticsCard from './VitalAnalyticsCard';
import ExportModal from './ExportModal';
import PhotoViewerModal from './PhotoViewerModal';
import ImportantInformationSection from './ImportantInformationSection';
import { parseDateString } from '../utils';
import { REPORT_PROMPT_DISMISS_KEY } from '../cache/keys';

const VITAL_DETAILS: { [key: string]: { label: string; unit: string } } = {
  'BP': { label: 'Blood Pressure', unit: 'mmHg' },
  'HR': { label: 'Heart Rate', unit: 'bpm' },
  'Temp': { label: 'Temperature', unit: '°C' },
  'SpO2': { label: 'SpO2', unit: '%' },
  'Glucose': { label: 'Glucose', unit: 'mg/dL' },
  'Respiration': { label: 'Respiration', unit: 'breaths/min' },
};


interface PatientInfoPanelProps {
  patient: Patient;
  records: HealthRecord[];
  vitalHistory: VitalHistory[];
  rawVitals?: unknown;
  importantNotes?: ImportantNote[];
  residentVisitations?: ResidentVisitation[];
  onSaveImportantNoteEdit?: (id: number, notes: string) => Promise<{ success: true } | { success: false; error: string }>;
  onUnflagImportantNote?: (id: number) => Promise<{ success: true } | { success: false; error: string }>;
  onMarkVisitCompleted?: (id: number) => Promise<{ success: true } | { success: false; error: string }>;
  onEnterInfoClick: () => void;
  onBookAppointmentClick: () => void;
  onRefreshClick: () => void;
  onLogoutClick: () => void;
  refreshProgress: number | null;
  userRole: 'staff' | 'family';
  onShowMore?: () => void;
  hasMoreRecords?: boolean;
  isLoadingMore?: boolean;
}

function FilterIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
    )
}

function ExportIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
    )
}

const RefreshButton: React.FC<{ onClick: () => void; progress: number | null }> = ({ onClick, progress }) => {
    const isRefreshing = progress !== null;
    const size = 44; // Button size in pixels
    const strokeWidth = 3.5;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = isRefreshing ? circumference - ((progress ?? 0) / 100) * circumference : circumference;

    return (
        <button
            onClick={onClick}
            title="Refresh Data"
            className="relative w-11 h-11 flex items-center justify-center bg-light-gray hover:bg-primary-light text-primary p-3 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 disabled:bg-light-gray disabled:opacity-70 disabled:cursor-not-allowed"
            aria-label="Refresh patient data"
            disabled={isRefreshing}
        >
            {/* Progress Circle SVG */}
            {isRefreshing && (
                <svg
                    className="absolute top-0 left-0 w-full h-full"
                    width={size}
                    height={size}
                    viewBox={`0 0 ${size} ${size}`}
                >
                    <circle
                        className="text-primary-light/30"
                        stroke="currentColor"
                        strokeWidth={strokeWidth}
                        fill="transparent"
                        r={radius}
                        cx={size / 2}
                        cy={size / 2}
                    />
                    <circle
                        className="text-primary transition-stroke-dashoffset duration-300 ease-linear"
                        stroke="currentColor"
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        fill="transparent"
                        r={radius}
                        cx={size / 2}
                        cy={size / 2}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    />
                </svg>
            )}
            
            {/* Icon SVG */}
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M23 4v6h-6 M1 20v-6h6 M20.49 9A9 9 0 005.64 5.64L1 10 M3.51 15A9 9 0 0018.36 18.36L23 14" />
            </svg>
        </button>
    );
};

const PatientInfoPanel: React.FC<PatientInfoPanelProps> = ({ patient, records, vitalHistory, rawVitals, importantNotes, residentVisitations, onSaveImportantNoteEdit, onUnflagImportantNote, onMarkVisitCompleted, onEnterInfoClick, onBookAppointmentClick, onRefreshClick, onLogoutClick, refreshProgress, userRole, onShowMore, hasMoreRecords, isLoadingMore }) => {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const encryptedParam = params.get('customerId');
  const encCustomerId = encodeURIComponent(encryptedParam ?? '0');

  const [visibleRecordsCount, setVisibleRecordsCount] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState<HealthRecord['type'] | 'All'>('All');
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grouped' | 'list'>('grouped');
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const [showProfessionalPrompt, setShowProfessionalPrompt] = useState(false);
  const [pendingAction, setPendingAction] = useState<'export' | 'showMore' | null>(null);

  // 24-hour dismissal for the "View Health Monthly Report?" prompt.
  // Key is scoped by customer ID to prevent data leakage between patients.
  const reportPromptKey = REPORT_PROMPT_DISMISS_KEY(patient.id);
  const isReportPromptDismissed = (): boolean => {
    try {
      const ts = localStorage.getItem(reportPromptKey);
      if (!ts) return false;
      return Date.now() - Number(ts) < 24 * 60 * 60 * 1000;
    } catch { return false; }
  };
  const dismissReportPrompt = () => {
    try { localStorage.setItem(reportPromptKey, String(Date.now())); } catch {}
  };

  // New state for photo viewer
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [photoViewerImages, setPhotoViewerImages] = useState<string[]>([]);
  const [photoViewerInitialIndex, setPhotoViewerInitialIndex] = useState(0);

  const openPhotoViewer = (images: string[], index: number) => {
    setPhotoViewerImages(images);
    setPhotoViewerInitialIndex(index);
    setIsPhotoViewerOpen(true);
  };


  const showMoreRecords = () => {
    // Reveal more of the records already loaded in memory. If every loaded
    // record is already visible but the server still has older pages, fetch
    // the next page from the API and bump the visible cap so the newly
    // appended records show on this same click.
    if (visibleRecordsCount >= filteredRecords.length) {
      if (hasMoreRecords && onShowMore) {
        onShowMore();
        setVisibleRecordsCount(prevCount => prevCount + 20);
      }
      return;
    }
    setVisibleRecordsCount(prevCount => prevCount + 10);
  };

  const getCategoryColor = (type: HealthRecord['type']) => {
    switch (type) {
        case 'Vitals': return 'bg-accent';
        case 'ADL': return 'bg-primary-light';
        case 'Notes': return 'bg-accent-green';
        case 'Emotion': return 'bg-accent-yellow';
        case 'Photos': return 'bg-primary';
        case 'Physiotherapy': return 'bg-blue-500';
        case 'Appointment': return 'bg-purple-500';
        case 'Input / Output': return 'bg-teal-500';
        default: return 'bg-gray-400';
    }
  }
  
  // Logic to close filter panel on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
        if (filterPanelRef.current && !filterPanelRef.current.contains(event.target as Node)) {
            setIsFilterPanelOpen(false);
        }
    }
    document.addEventListener("click", handleClickOutside);
    return () => {
        document.removeEventListener("click", handleClickOutside);
    };
  }, [filterPanelRef]);
  
  const categoryFilters: (HealthRecord['type'] | 'All')[] = ['All', 'Vitals', 'ADL', 'Input / Output', 'Physiotherapy', 'Appointment', 'Notes', 'Emotion', 'Photos'];
  const dateFilters = [
    { id: 'all', label: 'All Time' },
    { id: 'today', label: 'Today' },
    { id: '3days', label: 'Last 3 Days' },
    { id: '7days', label: 'Last 7 Days' },
    { id: 'month', label: 'This Month' },
  ];
  
  const filteredRecords = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return records.filter(record => {
        if (!record || !record.date || !record.type) return false;
        const recordDate = parseDateString(record.date);
        
        let dateMatch = true;
        if (selectedDateRange !== 'all') {
            switch (selectedDateRange) {
                case 'today':
                    dateMatch = recordDate.toDateString() === now.toDateString();
                    break;
                case '3days':
                    const threeDaysAgo = new Date(today);
                    threeDaysAgo.setDate(today.getDate() - 2);
                    dateMatch = recordDate >= threeDaysAgo;
                    break;
                case '7days':
                    const sevenDaysAgo = new Date(today);
                    sevenDaysAgo.setDate(today.getDate() - 6);
                    dateMatch = recordDate >= sevenDaysAgo;
                    break;
                case 'month':
                    dateMatch = recordDate.getMonth() === now.getMonth() && recordDate.getFullYear() === now.getFullYear();
                    break;
                default:
                    break;
            }
        }

        if (!dateMatch) return false;

        const categoryMatch = selectedCategory === 'All' || record.type === selectedCategory;
        
        return categoryMatch;
    });
  }, [records, selectedCategory, selectedDateRange]);

  useEffect(() => {
    setVisibleRecordsCount(10);
  }, [selectedCategory, selectedDateRange]);

  const activeFilterCount = (selectedCategory !== 'All' ? 1 : 0) + (selectedDateRange !== 'all' ? 1 : 0);
  
  const clearFilters = () => {
    setSelectedCategory('All');
    setSelectedDateRange('all');
    setIsFilterPanelOpen(false);
  };
  
  const formatRecordDate = (dateString: string) => {
    try {
      if (!dateString) return '';
      const date = parseDateString(dateString);
      if (isNaN(date.getTime())) return dateString;
      return new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true,
      }).format(date);
    } catch {
      return dateString || '';
    }
  };

    const RecordDetails: React.FC<{ record: HealthRecord }> = ({ record }) => {
    if (!record || !record.summary) return null;
    const vitals = record.type === 'Vitals' ? (record.summary || '').split(',')
        .map(part => {
            const bits = part.split(':');
            if (bits.length < 2) return null;
            const label = bits[0].trim();
            const value = bits.slice(1).join(':').trim();
            return { label, value };
        })
        .filter((v): v is { label: string; value: string } => v !== null && v.value !== '') : [];

    return (
        <>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                <div className="flex-1">
                    {record.type === 'Vitals' && vitals.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2">
                            {vitals.map(({ label, value }) => {
                                const detail = VITAL_DETAILS[label] || { label: label, unit: '' };
                                let isHighBP = false;
                                if (label === 'BP') {
                                    const [systolicStr, diastolicStr] = value.split('/');
                                    const systolic = parseInt(systolicStr, 10);
                                    const diastolic = parseInt(diastolicStr, 10);
                                    if (!isNaN(systolic) && !isNaN(diastolic) && (systolic > 140 || diastolic > 90)) {
                                        isHighBP = true;
                                    }
                                }
                                return (
                                    <div key={label}>
                                        <div className="text-sm text-gray-500">{detail.label}</div>
                                        <div className={`font-semibold flex items-center gap-1 ${isHighBP ? 'text-red-600' : 'text-gray-800'}`}>
                                            {isHighBP && (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-accent-yellow" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 3.001-1.742 3.001H4.42c-1.53 0-2.493-1.667-1.743-3.001l5.58-9.92zM10 13a1 1 0 110-2 1 1 0 010 2zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                            <span>{value}</span>
                                            {detail.unit && <span className={`ml-1 text-sm font-normal ${isHighBP ? 'text-red-500' : 'text-gray-600'}`}>{detail.unit}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : record.type === 'Physiotherapy' ? (
                        <div className="text-gray-700">
                            {(() => {
                                const text = record.summary || '';
                                
                                // Check if already HTML-formatted (new format from HealthInputPanel)
                                if (text.startsWith('<div') || text.startsWith('<span')) {
                                    return <div dangerouslySetInnerHTML={{ __html: text }} />;
                                }

                                // Parse legacy format: "Pain on Movement: [X/10]; Physio Session: [...]; Notes/Observations: [...]"
                                // or "Notes: [...]"
                                const painMatch = text.match(/Pain on Movement:\s*\[([^\]]*)\]/);
                                const sessionMatch = text.match(/Physio Session:\s*\[([^\]]*)\]/);
                                
                                // Notes may contain ] inside HTML, find the notes section start
                                const notesMarkers = ['Notes/Observations: [', 'Notes: ['];
                                let notesContent = '';
                                for (const marker of notesMarkers) {
                                    const idx = text.indexOf(marker);
                                    if (idx >= 0) {
                                        notesContent = text.slice(idx + marker.length, text.lastIndexOf(']'));
                                        break;
                                    }
                                }

                                const painValue = painMatch?.[1] || '';
                                const sessionValue = sessionMatch?.[1] || '';
                                const painNum = parseInt(painValue);
                                const hasPain = painValue && painValue !== '0/10';
                                const hasSession = sessionValue.trim().length > 0;
                                const hasNotes = notesContent.trim().length > 0;

                                // Check if notes content looks like HTML (has tags or stripped tags)
                                const notesIsHtml = hasNotes && (
                                    notesContent.includes('<') || 
                                    notesContent.includes('&') ||
                                    // Detect stripped HTML (tags without angle brackets like "pspanHello/span/p")
                                    /^p(?:span|strong|em|code|b|i|u|ol|ul|li|br|h\d)/.test(notesContent.trim())
                                );

                                // If nothing parsed at all, show raw text with whitespace preserved
                                if (!painMatch && !sessionMatch && !hasNotes) {
                                    return <p className="text-sm text-gray-700 whitespace-pre-line">{text}</p>;
                                }

                                return (
                                    <div className="space-y-3">
                                        {/* Pain badge */}
                                        {hasPain && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pain</span>
                                                <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                                                    painNum >= 7 ? 'bg-red-100 text-red-700' :
                                                    painNum >= 4 ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-green-100 text-green-700'
                                                }`}>
                                                    {painValue}
                                                </span>
                                            </div>
                                        )}

                                        {/* Activities as pill tags */}
                                        {hasSession && (
                                            <div>
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Activities</span>
                                                <div className="flex flex-wrap gap-1">
                                                    {sessionValue.split(',').map((act, i) => (
                                                        <span key={i} className="inline-block px-2 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded-full border border-purple-200">
                                                            {act.trim()}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Notes with HTML rendering */}
                                        {hasNotes && (
                                            <div className="p-3 bg-gray-50 rounded-lg" style={{ borderLeft: '3px solid #7C3AED' }}>
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Notes / Observations</span>
                                                {notesIsHtml ? (
                                                    <div 
                                                        className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                                                        dangerouslySetInnerHTML={{ __html: 
                                                            // If it has proper HTML tags, render directly
                                                            notesContent.includes('<') ? notesContent.trim() :
                                                            // If it has stripped tags (no angle brackets), try to add them back
                                                            notesContent.trim()
                                                                .replace(/(\/?)(p|span|strong|em|code|b|i|u|ol|ul|li|br|h[1-6])/g, '<$1$2>')
                                                                .replace(/<br>/g, '<br/>')
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                                                        {notesContent.trim()}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    ) : (
                        <p className="text-gray-700">{record.summary}</p>
                    )}
                </div>
                <div className="flex-shrink-0 md:ml-4 text-left md:text-right mt-2 md:mt-0">
                    <p className="text-sm text-gray-500">
                        {record.date ? formatRecordDate(record.date) : ''} - by {record.recordedBy || 'Unknown'}
                    </p>
                </div>
            </div>
            
            {(record.type === 'Photos' || record.type === 'Physiotherapy') && record.images && record.images.filter(u => u && u !== 'null').length > 0 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {record.images.filter(u => u && u !== 'null').map((img, index) => (
                         <button 
                            key={index} 
                            className="flex-shrink-0 w-24 h-24 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg"
                            onClick={() => openPhotoViewer(record.images!.filter(u => u && u !== 'null'), index)}
                            aria-label={`View photo ${index + 1}`}
                        >
                            <img src={img} alt={`record photo ${index + 1}`} className="w-full h-full object-cover rounded-lg shadow-sm" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        </button>
                    ))}
                </div>
            )}
        </>
    );
  };
  
  const renderRecordGroup = (group: HealthRecord[], key: string) => {
    if (!group || group.length === 0) return null;

    const firstRecord = group[0];
    if (!firstRecord || !firstRecord.type) return null;
    const category = firstRecord.type;
    const colorClass = getCategoryColor(category);

    // Special handling for 'Photos' category to display images horizontally
    if (category === 'Photos') {
        const allImages = group.flatMap(record => (record.images || []).filter(u => u && u !== 'null'));
        // Create a flat list of records corresponding to each image, for metadata.
        const imageRecords = group.flatMap(record => 
            (record.images || []).filter(u => u && u !== 'null').map(() => record)
        );

        return (
            <div key={key} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="p-4 flex items-center justify-between border-b border-light-gray bg-gray-50/50">
                    <span className={`px-3 py-1 text-sm rounded-full text-white ${colorClass}`}>
                        {category}
                    </span>
                    <span className="text-sm text-gray-500 font-medium">
                        {allImages.length} {allImages.length > 1 ? 'photos' : 'photo'}
                    </span>
                </div>
                <div className="p-4">
                    <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4">
                        {allImages.map((img, index) => {
                            const record = imageRecords[index]; // Get corresponding record for metadata
                            return (
                                <div key={index} className="flex-shrink-0 w-32 text-center">
                                    <button 
                                        className="w-full h-32 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg block"
                                        onClick={() => openPhotoViewer(allImages, index)}
                                        aria-label={`View photo ${index + 1}`}
                                    >
                                        <img src={img} alt={`record photo ${index + 1}`} className="w-full h-full object-cover rounded-lg shadow-sm" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                    </button>
                                    {record && (
                                        <div className="text-xs text-gray-500 mt-1.5">
                                            <p className="truncate">{record.date ? formatRecordDate(record.date) : ''}</p>
                                            <p className="truncate">by {record.recordedBy || 'Unknown'}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    // Default rendering for all other categories
    return (
        <div key={key} className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b border-light-gray bg-gray-50/50">
                <span className={`px-3 py-1 text-sm rounded-full text-white ${colorClass}`}>
                    {category}
                </span>
                <span className="text-sm text-gray-500 font-medium">
                    {group.length} {group.length > 1 ? 'entries' : 'entry'}
                </span>
            </div>
            
            <div className="divide-y divide-light-gray">
                {group.map(record => (
                    <div key={record.id} className="p-4 hover:bg-light-bg/50 transition-colors duration-200">
                        <RecordDetails record={record} />
                    </div>
                ))}
            </div>
        </div>
    );
};

const renderListItem = (record: HealthRecord) => {
    if (!record || !record.id || !record.type) return null;
    const colorClass = getCategoryColor(record.type);
    return (
        <div key={record.id} className="bg-white rounded-2xl shadow-sm overflow-hidden p-4">
            <div className="flex items-start justify-between mb-3">
                <span className={`px-3 py-1 text-sm rounded-full text-white ${colorClass}`}>
                    {record.type}
                </span>
            </div>
            <RecordDetails record={record} />
        </div>
    );
};

  return (
    <div className="space-y-8">
      <div className="p-6 bg-white rounded-2xl shadow-sm flex flex-col md:flex-row items-center md:items-center gap-4 md:gap-6">
        <img src={patient.avatarUrl} alt={patient.name} className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-primary-light flex-shrink-0" />
        <div className="flex-1 text-center md:text-left">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{patient.name}</h2>
          <p className="text-sm md:text-base text-gray-500">
            {patient.age > 0 ? `${patient.age} years old | ` : ''}Care ID: {patient.id} | {patient.facility}
          </p>
        </div>
        <div className="w-full md:w-auto mt-4 md:mt-0 md:ml-auto flex flex-col sm:flex-row items-center gap-3">
           <button 
                onClick={onBookAppointmentClick}
                className="w-full md:w-auto bg-primary-light hover:bg-primary text-primary hover:text-white font-bold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21v-1a6 6 0 00-1.781-4.121M12 10.875a4 4 0 100-5.292M12 10.875a4 4 0 110 5.292" /></svg>
                Visit Your Loved One
            </button>
           {userRole === 'staff' && (
            <button 
                    onClick={onEnterInfoClick}
                    className="w-full md:w-auto bg-accent hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Health Info
                </button>
            )}
            {userRole === 'staff' && (
              <button
                onClick={() => navigate(`/health-tracker/professional?customerId=${encCustomerId}`)}
                className="w-full md:w-auto bg-primary hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Health Monthly Report
              </button>
            )}
            <div className="flex items-center gap-3">
                <RefreshButton onClick={onRefreshClick} progress={refreshProgress} />
                <button
                    onClick={onLogoutClick}
                    title="Logout"
                    className="w-11 h-11 flex items-center justify-center bg-light-gray hover:bg-red-100 text-gray-500 hover:text-red-600 p-3 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    aria-label="Logout"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                </button>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <ImportantInformationSection
          importantNotes={importantNotes ?? []}
          residentVisitations={residentVisitations ?? []}
          userRole={userRole}
          onSaveEdit={onSaveImportantNoteEdit ?? (async () => ({ success: false, error: 'Not available' }))}
          onUnflag={onUnflagImportantNote ?? (async () => ({ success: false, error: 'Not available' }))}
          onMarkCompleted={onMarkVisitCompleted ?? (async () => ({ success: false, error: 'Not available' }))}
        />

        <VitalAnalyticsCard data={vitalHistory} rawData={rawVitals} />
      </div>

      <div className="p-6 bg-white rounded-2xl shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 pb-4 border-b border-light-gray gap-4">
          <h3 className="text-xl font-bold">Recent Health Records</h3>
          <div className="flex items-center gap-2">
            <div className="bg-light-gray p-1 rounded-lg flex items-center">
                <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors w-full ${viewMode === 'list' ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-primary'}`}
                >
                    List
                </button>
                <button
                    onClick={() => setViewMode('grouped')}
                    className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors w-full ${viewMode === 'grouped' ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-primary'}`}
                >
                    Grouped
                </button>
            </div>
            <button
                onClick={() => {
                  if (userRole === 'staff') {
                    if (isReportPromptDismissed()) {
                      setIsExportModalOpen(true);
                    } else {
                      setPendingAction('export');
                      setShowProfessionalPrompt(true);
                    }
                  } else {
                    setIsExportModalOpen(true);
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 bg-light-gray text-gray-700 hover:bg-primary-light"
            >
                <ExportIcon />
                <span>Export</span>
            </button>
            <div className="relative" ref={filterPanelRef}>
              <button
                  onClick={() => setIsFilterPanelOpen(prev => !prev)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 bg-light-gray text-gray-700 hover:bg-primary-light"
              >
                  <FilterIcon />
                  <span>Filter</span>
                  {activeFilterCount > 0 && (
                      <span className="bg-primary text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                          {activeFilterCount}
                      </span>
                  )}
              </button>

              {isFilterPanelOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-light-gray z-20 origin-top-right">
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-600 mb-2 block">Filter by Category</label>
                      <div className="flex flex-wrap gap-2">
                        {categoryFilters.map(category => (
                          <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                              selectedCategory === category
                                ? 'bg-primary text-white shadow'
                                : 'bg-light-gray text-gray-700 hover:bg-primary-light'
                            }`}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600 mb-2 block">Filter by Date</label>
                      <div className="flex flex-wrap gap-2">
                        {dateFilters.map(filter => (
                          <button
                            key={filter.id}
                            onClick={() => setSelectedDateRange(filter.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                              selectedDateRange === filter.id
                                ? 'bg-primary text-white shadow'
                                : 'bg-light-gray text-gray-700 hover:bg-primary-light'
                            }`}
                          >
                            {filter.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {activeFilterCount > 0 && (
                    <div className="p-2 border-t border-light-gray bg-gray-50 rounded-b-xl">
                      <button
                        onClick={clearFilters}
                        className="w-full text-center text-sm font-medium text-primary hover:bg-primary-light rounded-md py-1.5 transition-colors"
                      >
                        Clear Filters
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {filteredRecords.length > 0 ? (
          <div className="space-y-4">
             {viewMode === 'grouped' ? (
                (() => {
                    const recordsToRender = filteredRecords.slice(0, visibleRecordsCount);
                    if (recordsToRender.length === 0) return null;

                    const groupedElements: React.ReactElement[] = [];
                    let currentGroup: HealthRecord[] = [];
                    let groupIndex = 0;

                    recordsToRender.forEach((record, index) => {
                        if (!record || !record.type || !record.id) return;
                        const prevRecord = index > 0 ? recordsToRender[index - 1] : null;
                        
                        if (prevRecord && prevRecord.type !== record.type) {
                            const groupElement = renderRecordGroup(currentGroup, `group-${groupIndex}-${currentGroup[0]?.id || index}`);
                            if (groupElement) {
                                groupedElements.push(groupElement);
                            }
                            currentGroup = [];
                            groupIndex++;
                        }
                        
                        currentGroup.push(record);
                        
                        if (index === recordsToRender.length - 1) {
                            const groupElement = renderRecordGroup(currentGroup, `group-${groupIndex}-${currentGroup[0]?.id || index}`);
                            if (groupElement) {
                                groupedElements.push(groupElement);
                            }
                        }
                    });

                    return groupedElements;
                })()
             ) : (
                filteredRecords.slice(0, visibleRecordsCount).map(record => renderListItem(record))
             )}
          </div>
        ) : (
           <div className="text-center py-10">
            <p className="text-gray-500 text-lg">No records match the selected filters.</p>
          </div>
        )}

        {(visibleRecordsCount < filteredRecords.length || hasMoreRecords) && (
          <div className="mt-6 text-center">
            <button
              disabled={isLoadingMore}
              onClick={() => {
                if (userRole === 'staff') {
                  if (isReportPromptDismissed()) {
                    showMoreRecords();
                  } else {
                    setPendingAction('showMore');
                    setShowProfessionalPrompt(true);
                  }
                } else {
                  showMoreRecords();
                }
              }}
              className="bg-primary-light hover:bg-primary text-primary hover:text-white font-bold py-2 px-6 rounded-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoadingMore ? 'Loading...' : 'Show More'}
            </button>
          </div>
        )}
      </div>

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        patient={patient}
        records={records}
        vitalHistory={vitalHistory}
      />

      {/* Health Monthly Report Prompt Modal */}
      {showProfessionalPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 m-4">
            <h3 className="text-lg font-bold text-primary mb-3">View Health Monthly Report?</h3>
            <p className="text-gray-600 text-sm mb-6">
              Would you like to view the Health Monthly Report with detailed analytics, charts, and exportable PDF?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  dismissReportPrompt();
                  setShowProfessionalPrompt(false);
                  if (pendingAction === 'export') {
                    setIsExportModalOpen(true);
                  } else if (pendingAction === 'showMore') {
                    showMoreRecords();
                  }
                  setPendingAction(null);
                }}
                className="px-5 py-2 rounded-lg font-medium bg-light-gray text-gray-700 hover:bg-gray-200 transition"
              >
                No, Continue
              </button>
              {userRole === 'staff' && (
                <button
                  onClick={() => {
                    setShowProfessionalPrompt(false);
                    setPendingAction(null);
                    navigate(`/health-tracker/professional?customerId=${encCustomerId}`);
                  }}
                  className="px-5 py-2 rounded-lg font-medium bg-primary text-white hover:bg-purple-700 transition"
                >
                  View Health Monthly Report
                </button>
              )}
            </div>
          </div>
        </div>
      )}
       <PhotoViewerModal
        isOpen={isPhotoViewerOpen}
        onClose={() => setIsPhotoViewerOpen(false)}
        images={photoViewerImages}
        initialIndex={photoViewerInitialIndex}
      />
    </div>
  );
};

export default PatientInfoPanel;
